import { QConfigStructureCategory, QConfigStructureType } from '@exam-management/graphql';

import { type QConfigStructureData } from '../../types';

function getQBSList(data: QConfigStructureData[]) {
  return data
    .filter((item) => item.category === QConfigStructureCategory.Course)
    .sort((a, b) => {
      const aQBS = data.find((item) => item.id === a.parentId);
      const bQBS = data.find((item) => item.id === b.parentId);

      return (aQBS?.order ?? 0) - (bQBS?.order ?? 0);
    });
}

function getPartList(data: QConfigStructureData[]) {
  return data
    .filter((item) => item.category === QConfigStructureCategory.Part)
    .sort((a, b) => {
      return (a.order ?? 0) - (b.order ?? 0);
    });
}

function getPartMapByQbsId(data: QConfigStructureData[]) {
  const partList = getPartList(data);

  return partList.reduce((acc, part) => {
    if (part.parentId) {
      const oldData = acc.get(part.parentId) ?? [];
      acc.set(part.parentId, [...oldData, part]);
    }
    return acc;
  }, new Map<string, QConfigStructureData[]>());
}

function getTableData(data: QConfigStructureData[]) {
  const tableData = data
    .filter(
      (item) =>
        item.type === QConfigStructureType.QuestionType ||
        item.type === QConfigStructureType.Tag ||
        (item.type === QConfigStructureType.QBankStructureLevelItem &&
          item.category !== QConfigStructureCategory.Course &&
          item.category !== QConfigStructureCategory.Part),
    )
    .sort((a, b) => {
      return (a.order ?? 0) - (b.order ?? 0);
    });

  const tableDataGroupByParentId = tableData.reduce((acc, item) => {
    const parentId = item.parentId ?? '';
    const oldData = acc.get(parentId) ?? [];
    acc.set(parentId, [...oldData, item]);
    return acc;
  }, new Map<string, QConfigStructureData[]>());

  sortTableData(tableDataGroupByParentId);

  const combinedTableData = new Map<string, QConfigStructureData[]>();
  const combineData = (items: QConfigStructureData[]) => {
    let newItems: QConfigStructureData[] = [];
    for (const item of items) {
      const itemChildren = tableDataGroupByParentId.get(item.id) ?? [];
      newItems = [...newItems, item, ...combineData(itemChildren)];
      tableDataGroupByParentId.delete(item.id);
    }
    return newItems;
  };

  const partList = getPartList(data);
  for (const part of partList) {
    const id = part.id;
    const items = tableDataGroupByParentId.get(id) ?? [];
    combinedTableData.set(id, combineData(items));
  }

  return combinedTableData;
}

function fillMissingData(data: QConfigStructureData[]) {
  return data.map((item) => {
    if (item.type === QConfigStructureType.QBankStructureLevelItem) {
      if (item.name === null) {
        if (item.order === 0) {
          return {
            ...item,
            name: 'ไม่ระบุ',
          };
        } else {
          return {
            ...item,
            name: 'ไม่เปิดใช้งาน',
          };
        }
      }
    }
    return item;
  });
}

export function formatQConfigStructureData(data: QConfigStructureData[]) {
  const filledData = fillMissingData(data);

  const qbsList = getQBSList(filledData);
  const partList = getPartMapByQbsId(filledData);
  const tableData = getTableData(filledData);

  return {
    qbsList,
    partList,
    tableData,
  };
}

const CATEGORY_ORDER = [
  QConfigStructureCategory.Course,
  QConfigStructureCategory.Part,
  QConfigStructureCategory.Subject,
  QConfigStructureCategory.Group,
  QConfigStructureCategory.Chapter,
  QConfigStructureCategory.Single,
  QConfigStructureCategory.ItemSet,
  QConfigStructureCategory.LearningObjectiveStatement,
  QConfigStructureCategory.Topic,
  QConfigStructureCategory.AllSubTopic,
  QConfigStructureCategory.SubTopic,
];

const QConfigStructureByOrder = new Map(CATEGORY_ORDER.map((category, index) => [category, index]));

function getCategoryOrder(category: QConfigStructureCategory) {
  return QConfigStructureByOrder.get(category) ?? CATEGORY_ORDER.length;
}

function sortTableData(tableDataByParentId: Map<string, QConfigStructureData[]>) {
  for (const [parentId, children] of tableDataByParentId.entries()) {
    //REMARK: Children of Chapter are SINGLE/ITEM_SET Level
    const isChildrenQuestionType =
      !!children.length &&
      children.every((child) => child.type === QConfigStructureType.QuestionType);

    //REMARK: Sort SINGLE/ITEM_SET Level by children category, category, and order
    if (isChildrenQuestionType) {
      const childrenForSorting = children.map((item) => {
        //REMARK: GrandChildren of Chapter are LOS and TOPIC Level
        const grandChildren = tableDataByParentId.get(item.id) ?? [];
        const isLOS =
          !!grandChildren.length &&
          grandChildren.every(
            (child) => child.category === QConfigStructureCategory.LearningObjectiveStatement,
          );
        const isTopic =
          !!grandChildren.length &&
          grandChildren.every((child) => child.category === QConfigStructureCategory.Topic);

        let childrenCategory = null;
        if (isLOS) {
          childrenCategory = QConfigStructureCategory.LearningObjectiveStatement;
        } else if (isTopic) {
          childrenCategory = QConfigStructureCategory.Topic;
        }

        return {
          structure: item,
          order: item.order ?? 0,
          category: item.category,
          childrenCategory,
        };
      });

      const sortedChildren = [...childrenForSorting]
        .sort((a, b) => {
          if (
            a.childrenCategory &&
            b.childrenCategory &&
            a.childrenCategory !== b.childrenCategory
          ) {
            const aChildrenCategoryOrder = getCategoryOrder(a.childrenCategory);
            const bChildrenCategoryOrder = getCategoryOrder(b.childrenCategory);
            return aChildrenCategoryOrder - bChildrenCategoryOrder;
          }

          if (a.category && b.category && a.category !== b.category) {
            const aCategoryOrder = getCategoryOrder(a.category);
            const bCategoryOrder = getCategoryOrder(b.category);
            return aCategoryOrder - bCategoryOrder;
          }

          return a.order - b.order;
        })
        .map((item) => item.structure);

      tableDataByParentId.set(parentId, sortedChildren);
    }
  }
}
